import { classNames } from '@panwds/tailwindcss';
import { useSelect } from 'downshift';
import React, { useMemo, useState, useEffect, cloneElement } from 'react';
import { v4 } from 'uuid';
import { noop } from '../../utils/noop.esm.js';
import { mergeRefs } from '../../utils/refs/refs.esm.js';
import { usePopper } from '../../utils/usePopper.esm.js';
import { POPOVER_PLACEMENTS } from '../../utils/constants.esm.js';
import { Button } from '../Button/Button.esm.js';
import '@panwds/icons';
import '../Icon/ImgIcon.esm.js';
import '../Icon/SemanticIcons.esm.js';
import '../Button/ProgressButton.esm.js';
import '../Button/CopyButton.esm.js';
import { Portal } from '../Portal/Portal.esm.js';
import { TruncateText } from '../TruncateText/TruncateText.esm.js';
import { Body } from '../Typography/Body.esm.js';
import { SelectList } from './SelectList.esm.js';
import { useDraftSelections } from './useDraftSelections.esm.js';
import { useSelectAll } from './useSelectAll.esm.js';
import { sortBySelected, onSelectedItemChange, isDomElement, selectedItemLabel } from './utils.esm.js';

const emptyItems = [];
/**
 * This component is like `Select` but does not handle searching in the Select at all.
 *
 * ### Import Guide
 *
 * ```jsx
 * import { SelectWithoutSearch } from "@panwds/react-ui";
 * ```
 */
function SelectWithoutSearch({ buttonRef, items = emptyItems, button, 'data-autofocus': dataAutofocus, dataMetrics, dataTestId, enableInitialFocus = false, disabled, defaultPlacement = 'bottom-start', enableSelectAll = false, fullWidth = false, isLoading = false, labelId, listItemStyle, toggleButtonId, darkMode, menuStyle, enableMultiSelect = false, onChange = noop, onMenuKeyEnter, onOpen, selectedItem, formatOption, fitToItemWidth = false, placeholder, customWidth, itemsShown = 10, listItemSize = 'md', addClassName, addButtonClassName, allowDraftSelections = false }) {
    var _a;
    const baseId = useMemo(() => `field-${v4()}-label`, []);
    const selectedListItems = (selectedItem ? [selectedItem].flat() : []);
    // needed for apply / cancel variant
    const [cachedItems, setSelectedItems] = useState(selectedListItems);
    useEffect(() => {
        // Set initial state of pillsCurrentlyShowing since useFilters initiates it to 0
        setSelectedItems((selectedItem ? [selectedItem].flat() : []));
    }, [selectedItem]);
    // needed for single / multi variants
    let selectedItems = selectedListItems;
    /**
     * use the state if following condition is met because the custom
     * hook would be updating it using setSelectedItems
     */
    if (allowDraftSelections && enableMultiSelect) {
        selectedItems = cachedItems;
    }
    const selectedValues = useMemo(() => selectedItems.map((item) => item.value), [selectedItems, cachedItems]);
    const { showSelectAll, selectAllItem, allSelected } = useSelectAll({
        items,
        enableSelectAll,
        selectedValues,
        enableMultiSelect
    });
    const [sortedItems, setSortedItems] = useState([...items].sort(sortBySelected(selectedValues)));
    const normalizedItems = enableSelectAll && showSelectAll ? [selectAllItem, ...sortedItems] : sortedItems;
    const { rawTriggerRef, triggerRef, containerRef } = usePopper({
        placement: defaultPlacement,
        strategy: 'fixed',
        ...(fitToItemWidth && {
            modifiers: [
                {
                    name: 'sameWidth',
                    enabled: true,
                    fn: ({ state }) => {
                        // todo: potential bug
                        state.styles['popper'].width = `${state.rects.reference.width}px`;
                    },
                    phase: 'beforeWrite',
                    requires: ['computeStyles']
                },
                {
                    name: 'flip',
                    options: {
                        fallbackPlacements: [
                            POPOVER_PLACEMENTS.BOTTOM_START,
                            POPOVER_PLACEMENTS.BOTTOM_END,
                            POPOVER_PLACEMENTS.TOP_START,
                            POPOVER_PLACEMENTS.TOP_END
                        ]
                    }
                }
            ]
        })
    });
    const { draftSelectionsAllowed, onDraftChange, onRealChange, applyChanges, discardChanges, selectionsChanged } = useDraftSelections({
        enableMultiSelect,
        allowDraftSelections,
        setSelectedItems,
        selectedItem,
        onChange,
        triggerRef: rawTriggerRef
    });
    const { isOpen, getItemProps, getMenuProps, highlightedIndex, getToggleButtonProps } = useSelect({
        items: normalizedItems,
        initialIsOpen: enableInitialFocus,
        labelId: labelId,
        id: baseId,
        toggleButtonId: toggleButtonId,
        itemToString(item) {
            return item ? String(item.value) : '';
        },
        stateReducer(state, actionAndChanges) {
            const { changes, type } = actionAndChanges;
            switch (type) {
                case useSelect.stateChangeTypes.MenuKeyDownEnter:
                case useSelect.stateChangeTypes.MenuKeyDownSpaceButton:
                case useSelect.stateChangeTypes.ItemClick:
                    return {
                        ...changes,
                        highlightedIndex: state.highlightedIndex,
                        isOpen: enableMultiSelect // keep menu open after selection.
                    };
                default:
                    return changes;
            }
        },
        selectedItem: enableMultiSelect ? null : selectedItems[0] || null,
        onSelectedItemChange: (changes) => {
            onSelectedItemChange({
                changes,
                enableMultiSelect,
                selectedItems,
                onChange: draftSelectionsAllowed ? onDraftChange : onRealChange,
                selectedValues,
                selectAllItem,
                allSelected,
                itemsForSelectAll: items
            });
        },
        onStateChange: ({ type, isOpen }) => {
            var _a;
            if (type === '__menu_blur__' && !isOpen) {
                (_a = rawTriggerRef === null || rawTriggerRef === void 0 ? void 0 : rawTriggerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }
        }
    });
    const addProps = isDomElement(button === null || button === void 0 ? void 0 : button.type.toString()) ? {} : { isOpen };
    const DefaultButton = (React.createElement(Button, { appearance: "tertiary", "data-autofocus": dataAutofocus, "data-testid": `${dataTestId !== null && dataTestId !== void 0 ? dataTestId : 'select'}-trigger`, fullWidth: fullWidth, isMenu: true, isOpen: isOpen, addClassName: addButtonClassName },
        React.createElement(TruncateText, { addClassName: classNames('tw-flex-auto tw-text-left', selectedItemLabel(selectedItem) ? 'tw-text-gray-900 dark:tw-text-gray-100' : 'tw-text-gray-500') }, selectedItemLabel(selectedItem) ||
            (placeholder && (React.createElement(Body, { appearance: "secondary", addClassName: classNames('tw-text-xs', !disabled && 'dark:group-hover:tw-text-gray-300 dark:group-active:tw-text-gray-200') }, placeholder))))));
    const trigger = cloneElement(button || DefaultButton, getToggleButtonProps({
        ...addProps,
        ...((_a = button === null || button === void 0 ? void 0 : button.props) !== null && _a !== void 0 ? _a : {}),
        ...(customWidth && { style: { width: customWidth } }),
        ref: buttonRef ? mergeRefs([buttonRef, triggerRef]) : triggerRef,
        disabled
    }));
    useEffect(() => {
        if (!isOpen && enableMultiSelect)
            setSortedItems([...items].sort(sortBySelected(selectedValues)));
        if (onOpen) {
            onOpen(isOpen);
        }
    }, [isOpen]);
    useEffect(() => {
        if (enableMultiSelect) {
            setSortedItems([...items].sort(sortBySelected(selectedValues)));
        }
        else {
            setSortedItems([...items]);
        }
    }, [items]);
    const draftSelectionsControl = draftSelectionsAllowed ? (React.createElement("div", { className: "tw-flex tw-justify-end tw-sticky tw-top-0 tw-z-10 tw-p-2 tw-bg-white dark:tw-bg-gray-1000" },
        React.createElement(Button, { appearance: "secondary", onClick: discardChanges, disabled: !selectionsChanged, addClassName: "tw-mr-2" }, "Cancel"),
        React.createElement(Button, { appearance: "primary", onClick: applyChanges, disabled: !selectionsChanged }, "Apply"))) : undefined;
    return (React.createElement("div", { className: classNames(darkMode && 'dark'), ...(customWidth && { style: { width: customWidth } }) },
        trigger,
        React.createElement(Portal, null,
            React.createElement("div", { className: classNames(addClassName, 'tw-z-30', darkMode && 'dark'), ref: containerRef, "data-testid": "portal-div" },
                React.createElement(SelectList, { dataMetrics: dataMetrics, dataTestId: dataTestId, isLoading: isLoading, isOpen: isOpen, listItemStyle: listItemStyle, menuStyle: menuStyle, getMenuProps: getMenuProps, onMenuKeyEnter: onMenuKeyEnter, items: normalizedItems, highlightedIndex: highlightedIndex, selectedItems: selectedItems, selectAllItem: selectAllItem, enableMultiSelect: enableMultiSelect, getItemProps: getItemProps, showSelectAll: showSelectAll, selectedValues: selectedValues, formatOption: formatOption, fitToItemWidth: fullWidth ? false : fitToItemWidth, itemsShown: itemsShown, listItemSize: listItemSize, draftSelectionsControl: draftSelectionsControl })))));
}

export { SelectWithoutSearch };
//# sourceMappingURL=SelectWithoutSearch.esm.js.map
